"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { LiveWaveform } from "@ui/live-waveform"
import { Triangle } from "lucide-react"
import { toast } from "sonner"

import { pickAudioMimeType, stopRecorderToBlob, transcribeWithDeepgram } from "@/lib/voice/recording"

interface VoiceTranscriptionPillProps {
  onClose: () => void
  focusedElement?: Element | null
  selectionStart?: number | null
  selectionEnd?: number | null
  keywords?: string[]
}

export interface VoiceTranscriptionPillRef {
  stopAndInsert: () => void
}

type Phase = "preparing" | "recording" | "transcribing" | "error"

function insertIntoElement(
  element: Element | null | undefined,
  transcript: string,
  selectionStart?: number | null,
  selectionEnd?: number | null
) {
  if (!element) return

  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    element.focus()
    const start = selectionStart ?? element.selectionStart ?? 0
    const end = selectionEnd ?? element.selectionEnd ?? 0
    const value = element.value

    element.value = value.substring(0, start) + transcript + value.substring(end)
    const newPosition = start + transcript.length
    element.setSelectionRange(newPosition, newPosition)

    element.dispatchEvent(new Event("input", { bubbles: true }))
    element.dispatchEvent(new Event("change", { bubbles: true }))
    element.focus()
    return
  }

  if (element.hasAttribute("contenteditable")) {
    if (element instanceof HTMLElement) element.focus()
    document.execCommand("insertText", false, transcript)
  }
}

export const VoiceTranscriptionPill = forwardRef<VoiceTranscriptionPillRef, VoiceTranscriptionPillProps>(
  (
    {
      onClose,
      focusedElement: initialFocusedElement,
      selectionStart: initialSelectionStart,
      selectionEnd: initialSelectionEnd,
      keywords,
    },
    ref
  ) => {
    const [phase, setPhase] = useState<Phase>("preparing")
    const [error, setError] = useState<string | null>(null)

    const mountedRef = useRef(true)
    const streamRef = useRef<MediaStream | null>(null)
    const recorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<BlobPart[]>([])

    const cleanup = useCallback(() => {
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop()
        }
      } catch {
        // Recorder already stopped
      }
      recorderRef.current = null

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      streamRef.current = null
      chunksRef.current = []
    }, [])

    useEffect(() => {
      mountedRef.current = true
      return () => {
        mountedRef.current = false
        cleanup()
      }
    }, [cleanup])

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault()
          onClose()
        }
      }

      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }, [onClose])

    const handleStreamReady = useCallback(
      (stream: MediaStream) => {
        if (!mountedRef.current) return

        try {
          streamRef.current = stream
          const mimeType = pickAudioMimeType()
          const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)

          recorderRef.current = recorder
          chunksRef.current = []

          recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              chunksRef.current.push(e.data)
            }
          }

          recorder.onerror = () => {
            if (!mountedRef.current) return
            setPhase("error")
            setError("Recording error")
            toast.error("Recording error")
          }

          recorder.onstart = () => {
            if (mountedRef.current) setPhase("recording")
          }

          recorder.start()
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : "Failed to start recording"
          setPhase("error")
          setError(errorMessage)
          toast.error(errorMessage)
          setTimeout(() => onClose(), 1500)
        }
      },
      [onClose]
    )

    const handleStreamError = useCallback(
      (err: Error) => {
        if (!mountedRef.current) return
        const errorMessage = err.message || "Microphone access denied"
        setPhase("error")
        setError(errorMessage)
        toast.error(errorMessage)
        setTimeout(() => onClose(), 1500)
      },
      [onClose]
    )

    const handleStopAndInsert = useCallback(async () => {
      if (phase === "transcribing") return

      const recorder = recorderRef.current
      if (!recorder) {
        onClose()
        return
      }

      if (phase === "preparing") {
        onClose()
        return
      }

      try {
        setPhase("transcribing")

        const blob = await stopRecorderToBlob(recorder, chunksRef)
        cleanup()

        const transcript = await transcribeWithDeepgram(blob, keywords)
        if (!transcript.trim()) {
          toast.error("No speech detected")
          onClose()
          return
        }

        navigator.clipboard.writeText(transcript)
        toast.success("Copied to clipboard")

        insertIntoElement(initialFocusedElement, transcript, initialSelectionStart, initialSelectionEnd)
        onClose()
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Transcription failed"
        setPhase("error")
        setError(errorMessage)
        toast.error(errorMessage)
        setTimeout(() => onClose(), 1500)
      }
    }, [phase, cleanup, onClose, initialFocusedElement, initialSelectionStart, initialSelectionEnd, keywords])

    useImperativeHandle(
      ref,
      () => ({
        stopAndInsert: handleStopAndInsert,
      }),
      [handleStopAndInsert]
    )

    const isRecording = phase === "recording"
    const isTranscribing = phase === "transcribing"
    const isPreparing = phase === "preparing"

    return (
      <div
        className="animate-in fade-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        data-voice-transcription-pill
      >
        <div className="bg-secondary flex items-center gap-4 rounded-xl px-4 py-0 shadow-lg">
          <div className="text-muted-foreground">
            <Triangle className="size-5 fill-current" />
          </div>

          <div className="flex-1">
            {error ? (
              <span className="text-destructive text-sm">{error}</span>
            ) : (
              <div className="text-foreground w-[300px]">
                <LiveWaveform
                  active={isPreparing || isRecording}
                  processing={isTranscribing}
                  barWidth={2} // default: 3
                  barGap={2} // default: 1
                  sensitivity={1.5} // default: 1
                  onStreamReady={handleStreamReady}
                  onError={handleStreamError}
                />
              </div>
            )}
          </div>

          <button
            onClick={handleStopAndInsert}
            disabled={isPreparing || isTranscribing}
            className="text-foreground flex items-center gap-2 text-sm font-medium disabled:opacity-50"
          >
            Stop
            <span className="bg-background flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs">
              <span className="text-muted-foreground">&#8984;</span>
              <span>O</span>
            </span>
          </button>

          <button
            onClick={onClose}
            disabled={isTranscribing}
            className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm disabled:opacity-50"
          >
            Cancel
            <span className="bg-background rounded px-1.5 py-0.5 text-xs">esc</span>
          </button>
        </div>
      </div>
    )
  }
)

VoiceTranscriptionPill.displayName = "VoiceTranscriptionPill"
